import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowDown01Icon,
  ArrowUp01Icon
} from "@hugeicons-pro/core-stroke-rounded";
import {
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Show
} from "shared/components";
import { Loader } from "shared/components/Loader";
import { Checkbox, CheckboxSize } from "shared/components/forms/Checkbox";
import { BaseInput } from "shared/components/forms/BaseInput";
import { useDebouncedValue } from "shared/hooks/use-debounced-value";
import { PickerUnit } from "./pickerUnits";

interface TreeProperty {
  id: number;
  name: string;
  units: PickerUnit[];
}

interface TreeGroup {
  name: string;
  properties: TreeProperty[];
}

type IsEligible = (unit: PickerUnit) => boolean;
type IsSelected = (unit: PickerUnit) => boolean;

const UNGROUPED_LABEL = "Other properties";
const UNIT_ROW_CV =
  "[content-visibility:auto] [contain-intrinsic-size:auto_1.75rem]";
const PROPERTY_ROW_CV =
  "[content-visibility:auto] [contain-intrinsic-size:auto_3rem]";

const buildGroups = (units: PickerUnit[]): TreeGroup[] => {
  const order: string[] = [];
  const byGroup = new Map<string, Map<number, TreeProperty>>();

  units.forEach((unit) => {
    const property = unit.property;
    if (!property) return;

    const groupName = property.property_group?.name ?? UNGROUPED_LABEL;
    let properties = byGroup.get(groupName);
    if (!properties) {
      properties = new Map();
      byGroup.set(groupName, properties);
      order.push(groupName);
    }

    let treeProperty = properties.get(property.id);
    if (!treeProperty) {
      treeProperty = { id: property.id, name: property.name, units: [] };
      properties.set(property.id, treeProperty);
    }
    treeProperty.units.push(unit);
  });

  return order.map((name) => ({
    name,
    properties: [...(byGroup.get(name)?.values() ?? [])]
  }));
};

const filterGroups = (groups: TreeGroup[], search: string): TreeGroup[] => {
  if (!search) return groups;

  return groups
    .map((group) => ({
      name: group.name,
      properties: group.properties
        .map((property): TreeProperty | null => {
          if (property.name.toLowerCase().includes(search)) return property;
          const units = property.units.filter((unit) =>
            (unit.title ?? "").toLowerCase().includes(search)
          );
          return units.length > 0 ? { ...property, units } : null;
        })
        .filter((property): property is TreeProperty => property !== null)
    }))
    .filter((group) => group.properties.length > 0);
};

interface SelectionState {
  checked: boolean;
  indeterminate: boolean;
}

const selectionFor = (
  eligibleUnits: PickerUnit[],
  isSelected: IsSelected
): SelectionState => {
  if (eligibleUnits.length === 0)
    return { checked: false, indeterminate: false };
  const selectedCount = eligibleUnits.filter(isSelected).length;
  return {
    checked: selectedCount === eligibleUnits.length,
    indeterminate: selectedCount > 0 && selectedCount < eligibleUnits.length
  };
};

const collectEligibleUnits = (
  properties: TreeProperty[],
  isEligible: IsEligible
): PickerUnit[] =>
  properties.flatMap((property) => property.units.filter(isEligible));

const UnitRow = memo(
  ({
    unit,
    eligible,
    selected,
    onToggle
  }: {
    unit: PickerUnit;
    eligible: boolean;
    selected: boolean;
    onToggle: (unit: PickerUnit) => void;
  }) => (
    <Checkbox
      size={CheckboxSize.SM}
      checked={selected}
      disabled={!eligible}
      onChange={() => onToggle(unit)}
      label={unit.title}
      wrapperClassName={UNIT_ROW_CV}
    />
  )
);
UnitRow.displayName = "UnitRow";

const PropertyRow = memo(
  ({
    property,
    expanded,
    isSelected,
    isEligible,
    onToggleExpand,
    onToggleUnit,
    onToggleAll
  }: {
    property: TreeProperty;
    expanded: boolean;
    isSelected: IsSelected;
    isEligible: IsEligible;
    onToggleExpand: (propertyId: number) => void;
    onToggleUnit: (unit: PickerUnit) => void;
    onToggleAll: (units: PickerUnit[], select: boolean) => void;
  }) => {
    const eligibleUnits = useMemo(
      () => property.units.filter(isEligible),
      [property.units, isEligible]
    );
    const { checked, indeterminate } = selectionFor(eligibleUnits, isSelected);

    return (
      <div className={PROPERTY_ROW_CV}>
        <div className="flex items-center justify-between border-y border-alpha-40 bg-background-900 py-3 pl-6 pr-4">
          <Checkbox
            label={property.name}
            checked={checked}
            indeterminate={indeterminate}
            disabled={eligibleUnits.length === 0}
            onChange={() => onToggleAll(eligibleUnits, !checked)}
            size={CheckboxSize.SM}
            wrapperClassName="min-w-0"
          />
          <IconButton
            HugeIcon={expanded ? ArrowUp01Icon : ArrowDown01Icon}
            size={IconButtonSize.SM}
            variant={IconButtonVariant.TEXT_BASE}
            onClick={() => onToggleExpand(property.id)}
          />
        </div>
        <Show if={expanded}>
          <div className="px-4 py-3">
            <div className="space-y-2 pl-4">
              {property.units.map((unit) => (
                <UnitRow
                  key={unit.id}
                  unit={unit}
                  eligible={isEligible(unit)}
                  selected={isSelected(unit)}
                  onToggle={onToggleUnit}
                />
              ))}
            </div>
          </div>
        </Show>
      </div>
    );
  }
);
PropertyRow.displayName = "PropertyRow";

const GroupSection = memo(
  ({
    group,
    expanded,
    expandedProperties,
    isSelected,
    isEligible,
    onToggleExpand,
    onTogglePropertyExpand,
    onToggleUnit,
    onToggleAll
  }: {
    group: TreeGroup;
    expanded: boolean;
    expandedProperties: Set<number>;
    isSelected: IsSelected;
    isEligible: IsEligible;
    onToggleExpand: (group: TreeGroup) => void;
    onTogglePropertyExpand: (propertyId: number) => void;
    onToggleUnit: (unit: PickerUnit) => void;
    onToggleAll: (units: PickerUnit[], select: boolean) => void;
  }) => {
    const eligibleUnits = useMemo(
      () => collectEligibleUnits(group.properties, isEligible),
      [group.properties, isEligible]
    );
    const { checked, indeterminate } = selectionFor(eligibleUnits, isSelected);

    return (
      <div>
        <div className="flex items-center justify-between border-y border-alpha-40 bg-background-800 px-4 py-3">
          <Checkbox
            label={group.name}
            checked={checked}
            indeterminate={indeterminate}
            disabled={eligibleUnits.length === 0}
            onChange={() => onToggleAll(eligibleUnits, !checked)}
            size={CheckboxSize.SM}
            wrapperClassName="min-w-0"
            labelClassName="!text-content-1000"
          />
          <IconButton
            HugeIcon={expanded ? ArrowUp01Icon : ArrowDown01Icon}
            size={IconButtonSize.SM}
            variant={IconButtonVariant.TEXT_BASE}
            onClick={() => onToggleExpand(group)}
          />
        </div>

        <Show if={expanded}>
          <div>
            {group.properties.map((property) => (
              <PropertyRow
                key={property.id}
                property={property}
                expanded={expandedProperties.has(property.id)}
                isSelected={isSelected}
                isEligible={isEligible}
                onToggleExpand={onTogglePropertyExpand}
                onToggleUnit={onToggleUnit}
                onToggleAll={onToggleAll}
              />
            ))}
          </div>
        </Show>
      </div>
    );
  }
);
GroupSection.displayName = "GroupSection";

interface ListingSelectionTreeProps {
  units: PickerUnit[];
  isEligible: IsEligible;
  isSelected: IsSelected;
  onToggleUnit: (unit: PickerUnit) => void;
  onToggleMany: (units: PickerUnit[], select: boolean) => void;
  isLoading?: boolean;
}

export const ListingSelectionTree = ({
  units,
  isEligible,
  isSelected,
  onToggleUnit,
  onToggleMany,
  isLoading = false
}: ListingSelectionTreeProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch] = useDebouncedValue(searchTerm, 400);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const expandedGroupsRef = useRef(expandedGroups);
  expandedGroupsRef.current = expandedGroups;
  const [expandedProperties, setExpandedProperties] = useState<Set<number>>(
    new Set()
  );

  const search = debouncedSearch.trim().toLowerCase();
  const allGroups = useMemo(() => buildGroups(units), [units]);
  const groups = useMemo(
    () => filterGroups(allGroups, search),
    [allGroups, search]
  );

  useEffect(() => {
    if (!search) {
      setExpandedGroups(new Set());
      setExpandedProperties(new Set());
      return;
    }
    setExpandedGroups(new Set(groups.map((group) => group.name)));
    setExpandedProperties(
      new Set(
        groups.flatMap((group) =>
          group.properties.map((property) => property.id)
        )
      )
    );
  }, [search, groups]);

  const toggleGroup = useCallback((group: TreeGroup) => {
    const willCollapse = expandedGroupsRef.current.has(group.name);
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      willCollapse ? next.delete(group.name) : next.add(group.name);
      return next;
    });
    if (willCollapse)
      setExpandedProperties((prev) => {
        const next = new Set(prev);
        group.properties.forEach((property) => next.delete(property.id));
        return next;
      });
  }, []);

  const toggleProperty = useCallback(
    (propertyId: number) =>
      setExpandedProperties((prev) => {
        const next = new Set(prev);
        next.has(propertyId) ? next.delete(propertyId) : next.add(propertyId);
        return next;
      }),
    []
  );

  return (
    <>
      <div className="pt-4">
        <BaseInput
          id="listing-selection-search"
          type="search"
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder="Search properties or units"
        />
      </div>

      <div className="mt-5 min-h-0 flex-1 overflow-y-auto">
        <Show if={isLoading}>
          <div className="flex justify-center py-12">
            <Loader />
          </div>
        </Show>

        <Show if={!isLoading && groups.length === 0}>
          <p className="py-12 text-center text-sm text-content-400">
            No results found.
          </p>
        </Show>

        {groups.map((group) => (
          <GroupSection
            key={group.name}
            group={group}
            expanded={expandedGroups.has(group.name)}
            expandedProperties={expandedProperties}
            isSelected={isSelected}
            isEligible={isEligible}
            onToggleExpand={toggleGroup}
            onTogglePropertyExpand={toggleProperty}
            onToggleUnit={onToggleUnit}
            onToggleAll={onToggleMany}
          />
        ))}
      </div>
    </>
  );
};
